import React, { useState, useMemo } from 'react'
import {
    Calendar, Clock, MapPin, Users, Flame, Shirt, Target,
    Backpack, AlertTriangle, Zap, Loader2
} from 'lucide-react'
import { BottomSheet } from '../commons/BottomSheet'
import { TrainerCard } from './TrainerCard'
import { parseMetadata, fmtDate, fmtTimeRange, isSessionUpcoming } from './corporate-helpers'

/**
 * Session Detail BottomSheet.
 *
 * Props:
 *   session          - session object (with optional .trainer)
 *   isOpen / onClose - modal state
 *   enrolledIds      - Set of enrolled session IDs
 *   onEnroll(id)     - callback to enroll
 *   onCancelEnrollment(id) - callback to cancel enrollment (optional)
 */
export const SessionDetailSheet = ({ session, isOpen, onClose, enrolledIds, onEnroll, onCancelEnrollment }) => {
    const trainer = session?.trainer
    const meta = useMemo(() => parseMetadata(session?.metadata), [session?.metadata])
    const [enrolling, setEnrolling] = useState(false)
    const [cancelling, setCancelling] = useState(false)
    const alreadyEnrolled = enrolledIds?.has(session?.id)
    const upcoming = session?.sessionDate && isSessionUpcoming(session.sessionDate)

    const handleEnroll = async () => {
        if (!session?.id) return
        setEnrolling(true)
        try { await onEnroll(session.id) } finally { setEnrolling(false) }
    }

    const handleCancel = async () => {
        if (!session?.id || !onCancelEnrollment) return
        setCancelling(true)
        try { await onCancelEnrollment(session.id) } finally { setCancelling(false) }
    }

    return (
        <BottomSheet isOpen={isOpen} onClose={onClose} maxWidth="2xl">
            <BottomSheet.Header>
                <BottomSheet.Title>{session?.sessionTitle || 'Session'}</BottomSheet.Title>
                {session?.sessionType && (
                    <div className="flex items-center gap-2 mt-1">
                        <span className="inline-block px-2.5 py-0.5 rounded-full text-[10px] font-semibold bg-indigo-100 text-indigo-700 uppercase tracking-wide">
                            {session.sessionType}
                        </span>
                        {session.status && (
                            <span className="inline-block px-2.5 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-100 text-emerald-700 uppercase tracking-wide">
                                {session.status}
                            </span>
                        )}
                    </div>
                )}
            </BottomSheet.Header>

            <BottomSheet.Content>
                <div className="space-y-4">
                    {/* Date */}
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-indigo-50 flex items-center justify-center shrink-0">
                            <Calendar size={16} className="text-indigo-600" />
                        </div>
                        <div>
                            <p className="text-xs text-gray-500">Date</p>
                            <p className="text-sm font-semibold text-gray-900">{fmtDate(session?.sessionDate)}</p>
                        </div>
                    </div>

                    {/* Time */}
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-violet-50 flex items-center justify-center shrink-0">
                            <Clock size={16} className="text-violet-600" />
                        </div>
                        <div>
                            <p className="text-xs text-gray-500">Time</p>
                            <p className="text-sm font-semibold text-gray-900">
                                {fmtTimeRange(session?.startTime, session?.endTime)}
                            </p>
                        </div>
                    </div>

                    {/* Location */}
                    {session?.location && (
                        <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0">
                                <MapPin size={16} className="text-emerald-600" />
                            </div>
                            <div>
                                <p className="text-xs text-gray-500">Location</p>
                                <p className="text-sm font-semibold text-gray-900">{session.location}</p>
                            </div>
                        </div>
                    )}

                    {/* Max Slots */}
                    {session?.maxSlots && (
                        <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center shrink-0">
                                <Users size={16} className="text-amber-600" />
                            </div>
                            <div>
                                <p className="text-xs text-gray-500">Max Participants</p>
                                <p className="text-sm font-semibold text-gray-900">{session.maxSlots}</p>
                            </div>
                        </div>
                    )}

                    {/* Description */}
                    {session?.description && (
                        <div className="pt-2 border-t border-gray-100">
                            <p className="text-xs text-gray-500 mb-1">Description</p>
                            <p className="text-sm text-gray-700 leading-relaxed">{session.description}</p>
                        </div>
                    )}

                    {/* ─── Metadata Details ────────────────────────────── */}
                    {meta && (
                        <div className="pt-3 border-t border-gray-100 space-y-3">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Session Details</p>

                            {meta.intensity && (
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center shrink-0">
                                        <Flame size={14} className="text-red-500" />
                                    </div>
                                    <div>
                                        <p className="text-[11px] text-gray-500">Intensity</p>
                                        <p className="text-sm font-semibold text-gray-900 capitalize">{meta.intensity}</p>
                                    </div>
                                </div>
                            )}

                            {meta.expected_calories_burn && (
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center shrink-0">
                                        <Zap size={14} className="text-orange-500" />
                                    </div>
                                    <div>
                                        <p className="text-[11px] text-gray-500">Estimated Calorie Burn</p>
                                        <p className="text-sm font-semibold text-gray-900">{meta.expected_calories_burn}</p>
                                    </div>
                                </div>
                            )}

                            {meta.dress_code && (
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                                        <Shirt size={14} className="text-blue-500" />
                                    </div>
                                    <div>
                                        <p className="text-[11px] text-gray-500">Dress Code</p>
                                        <p className="text-sm font-semibold text-gray-900">{meta.dress_code}</p>
                                    </div>
                                </div>
                            )}

                            {meta.focus_area?.length > 0 && (
                                <div className="flex items-start gap-3">
                                    <div className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center shrink-0 mt-0.5">
                                        <Target size={14} className="text-purple-500" />
                                    </div>
                                    <div>
                                        <p className="text-[11px] text-gray-500">Focus Areas</p>
                                        <div className="flex flex-wrap gap-1.5 mt-1">
                                            {meta.focus_area.map((area, i) => (
                                                <span key={i} className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-50 text-purple-700 capitalize">
                                                    {area}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {meta.bring_items?.length > 0 && (
                                <div className="flex items-start gap-3">
                                    <div className="w-8 h-8 rounded-lg bg-teal-50 flex items-center justify-center shrink-0 mt-0.5">
                                        <Backpack size={14} className="text-teal-500" />
                                    </div>
                                    <div>
                                        <p className="text-[11px] text-gray-500">What to Bring</p>
                                        <ul className="mt-1 space-y-0.5">
                                            {meta.bring_items.map((item, i) => (
                                                <li key={i} className="text-sm text-gray-700 flex items-center gap-1.5">
                                                    <span className="w-1 h-1 rounded-full bg-teal-400 shrink-0" />
                                                    {item}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            )}

                            {meta.medical_warnings?.length > 0 && (
                                <div className="flex items-start gap-3">
                                    <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center shrink-0 mt-0.5">
                                        <AlertTriangle size={14} className="text-amber-600" />
                                    </div>
                                    <div>
                                        <p className="text-[11px] text-amber-700 font-semibold">Health Advisories</p>
                                        <ul className="mt-1 space-y-0.5">
                                            {meta.medical_warnings.map((warning, i) => (
                                                <li key={i} className="text-sm text-amber-800 flex items-start gap-1.5">
                                                    <span className="w-1 h-1 rounded-full bg-amber-400 shrink-0 mt-1.5" />
                                                    {warning}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Trainer */}
                    {trainer && (
                        <div className="pt-3 border-t border-gray-100">
                            <p className="text-xs text-gray-500 mb-2">Trainer</p>
                            <TrainerCard trainer={trainer} />
                        </div>
                    )}
                </div>
            </BottomSheet.Content>

            <BottomSheet.Footer>
                <div className="flex gap-3">
                    <BottomSheet.CloseButton variant="secondary">Close</BottomSheet.CloseButton>

                    {/* Not enrolled → show Enroll (upcoming only) */}
                    {upcoming && !alreadyEnrolled && (
                        <button
                            disabled={enrolling}
                            onClick={handleEnroll}
                            className="flex-1 px-4 py-3 rounded-xl font-semibold text-white bg-linear-to-r from-indigo-500 to-violet-500 hover:from-indigo-600 hover:to-violet-600 shadow-lg shadow-indigo-200 transition-colors disabled:opacity-50"
                        >
                            {enrolling ? <Loader2 size={16} className="animate-spin mx-auto" /> : 'Enroll'}
                        </button>
                    )}

                    {/* Already enrolled + upcoming → show Cancel Enrollment */}
                    {alreadyEnrolled && upcoming && onCancelEnrollment && (
                        <button
                            disabled={cancelling}
                            onClick={handleCancel}
                            className="flex-1 px-4 py-3 rounded-xl font-semibold text-red-600 bg-red-50 hover:bg-red-100 transition-colors disabled:opacity-50"
                        >
                            {cancelling ? <Loader2 size={16} className="animate-spin mx-auto" /> : 'Cancel Enrollment'}
                        </button>
                    )}

                    {/* Already enrolled + past → just show badge */}
                    {alreadyEnrolled && !upcoming && (
                        <div className="flex-1 px-4 py-3 rounded-xl font-semibold text-center text-emerald-700 bg-emerald-50">
                            ✓ Enrolled
                        </div>
                    )}
                </div>
            </BottomSheet.Footer>
        </BottomSheet>
    )
}
